import { memo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import type { InvoiceResponseDTO } from "../types/invoice.types";

const SELECT_CLASSES =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const invoiceEditSchema = z.object({
  externalId: z.string().max(255).optional(),
  value: z
    .number({ message: "Valor é obrigatório" })
    .min(0, "Valor não pode ser negativo"),
  status: z.enum([
    "PENDING",
    "ISSUED",
    "ERROR",
    "WAITING",
    "CANCELED",
    "EMISSION_DISABLED",
    "EMISSION_SKIPPED",
  ]),
  type: z.enum(["PAYMENT", "CANCELLATION"]),
  paymentMethod: z.enum(["PIX", "CREDIT_CARD", "DEBIT_CARD", "CASH"]),
  issuedAt: z.string().min(1, "Data de emissão é obrigatória"),
});

export type InvoiceEditFormData = z.infer<typeof invoiceEditSchema>;

interface InvoiceEditDialogProps {
  open: boolean;
  loading: boolean;
  invoice: InvoiceResponseDTO | null;
  onClose: () => void;
  onSubmit: (data: InvoiceEditFormData) => void;
}

/**
 * Converte ISO 8601 vindo da API para o formato aceito pelo input
 * `datetime-local` (`YYYY-MM-DDTHH:mm`).
 */
function isoToDatetimeLocal(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Dialog de edição de uma nota fiscal existente.
 *
 * Reseta o form a partir do `invoice` recebido sempre que o dialog é
 * reaberto. `rentalId` não é editável pelo admin — é trafegado pela
 * página orquestradora ao montar o request.
 *
 * @example
 * <InvoiceEditDialog
 *   open={dialogMode === 'edit'}
 *   loading={dialogLoading}
 *   invoice={selectedInvoice}
 *   onClose={closeDialog}
 *   onSubmit={handleEditSubmit}
 * />
 */
export const InvoiceEditDialog = memo(function InvoiceEditDialog({
  open,
  loading,
  invoice,
  onClose,
  onSubmit,
}: InvoiceEditDialogProps) {
  const form = useForm<InvoiceEditFormData>({
    resolver: zodResolver(invoiceEditSchema),
    defaultValues: {
      externalId: "",
      value: 0,
      status: "PENDING",
      type: "PAYMENT",
      paymentMethod: "PIX",
      issuedAt: "",
    },
  });

  useEffect(() => {
    if (!invoice || !open) return;
    form.reset({
      externalId: invoice.externalId ?? "",
      value: invoice.value,
      status: invoice.status,
      type: invoice.type,
      paymentMethod: invoice.paymentMethod,
      issuedAt: isoToDatetimeLocal(invoice.issuedAt),
    });
  }, [invoice, open, form]);

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar Nota Fiscal</DialogTitle>
          <DialogDescription>
            Atualize os dados da nota fiscal #{invoice?.id}.
          </DialogDescription>
        </DialogHeader>
        {invoice?.status === "ERROR" && invoice.errorDescription && (
          <div className="flex items-start gap-3 p-4 bg-destructive/10 rounded-md border border-destructive/20">
            <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-destructive">
                Erro na emissão
              </p>
              <p className="text-sm text-muted-foreground break-words">
                {invoice.errorDescription}
              </p>
              {invoice.attemptCount !== null && (
                <p className="text-xs text-muted-foreground mt-1">
                  Tentativas: {invoice.attemptCount}
                </p>
              )}
            </div>
          </div>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="externalId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ID Externo (FocusNFe)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="EXT-2024-001"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={field.value}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="issuedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Emissão *</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <FormControl>
                      <select {...field} className={SELECT_CLASSES}>
                        <option value="PENDING">Pendente</option>
                        <option value="ISSUED">Emitida</option>
                        <option value="ERROR">Erro</option>
                        <option value="WAITING">Aguardando</option>
                        <option value="CANCELED">Cancelada</option>
                        <option value="EMISSION_DISABLED">
                          Emissão desativada
                        </option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo *</FormLabel>
                    <FormControl>
                      <select {...field} className={SELECT_CLASSES}>
                        <option value="PAYMENT">Pagamento</option>
                        <option value="CANCELLATION">Cancelamento</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pagamento *</FormLabel>
                    <FormControl>
                      <select {...field} className={SELECT_CLASSES}>
                        <option value="PIX">PIX</option>
                        <option value="CREDIT_CARD">Crédito</option>
                        <option value="DEBIT_CARD">Débito</option>
                        <option value="CASH">Dinheiro</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
